import {
  type AddToCartFormData,
  type AddToCartHookProps,
  useAddToCartForm,
} from "~/features/addToCart/hooks/useAddToCartNew";
import { useLayoutEffect, useState } from "react";
import { getNetPrice } from "~/domains/product/utils/productDescriptorUtils";
import { FormProvider } from "react-hook-form";
import { LoadingModal } from "~/features/addToCart/components/AddToCartProcess/Modals/LoadingModal";
import { ErrorLoadingModal } from "~/features/addToCart/components/AddToCartProcess/Modals/ErrorLoadingModal";
import { AddToCartModal } from "~/features/addToCart/components/AddToCartProcess/Modals/AddToCartModal";
import { ConsumerViewAvailabilityModal } from "~/features/addToCart/components/AddToCartProcess/Modals/ConsumerViewAvailabilityModal";
import { ProductService } from "~/features/addToCart/api/ProductService";
import { selectProperAmountInCart } from "~/features/addToCart/redux/cartAmounts/cartAmountSelectors";
import { AddToCartStep, WizardContextProvider } from "~/features/addToCart/components/AddToCartProcess/AddToCartWizard";
import { Permission, userHasPermission } from "~/domains/user/utils/permissions";
import { getStore } from "~/app/store/getStore";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { ErrorSendingModal } from "~/features/addToCart/components/AddToCartProcess/Modals/ErrorSendingModal";

export type AddToCartSubmitHandler = (descriptor: ProductDescriptor, data: AddToCartFormData) => Promise<boolean>;

export interface AddToCartWizardProps extends AddToCartHookProps {
  restrictionQuery: string;
  closePopup: () => void;
  onSubmit: AddToCartSubmitHandler;
}

export const EditPartWizard = ({ restrictionQuery, closePopup, onSubmit, ...hookProps }: AddToCartWizardProps) => {
  const [formProps, productDescriptor] = useAddToCartForm(hookProps);
  const editMode = hookProps.editMode;

  const [step, setStep] = useState<AddToCartStep>(() => getInitialStep(productDescriptor));
  const [isSending, setIsSending] = useState(false);

  // Ensure we cycle through initial loading states to a loaded state
  useLayoutEffect(() => {
    setStep(getInitialStep(productDescriptor));
  }, [productDescriptor]);

  const isLoadedState = [AddToCartStep.LOADED_KIT, AddToCartStep.LOADED_DEFAULT].includes(step);

  const submit = async () => {
    setIsSending(true);
    const formData = { ...formProps.getValues() };
    const priceReference = formData.priceReference;
    Object.keys(formData.optionalParts).forEach((id) => {
      const part = { ...formData.optionalParts[id] };
      formData.optionalParts[id] = part;
      const { current: descriptor } = ProductService.getProductDescriptor(part.productId, priceReference);
      const totalQuantity =
        part.quantity +
        selectProperAmountInCart({
          productCode: descriptor.base.code,
          packCode: part.selectedPackCode,
          priceReference,
        })(getStore().getState());
      const price = getNetPrice({ product: descriptor, packCode: part.selectedPackCode, totalQuantity });
      part.netPrice = price;
      part.priceUnit = price?.unit ?? 1;
    });

    const result = await onSubmit(productDescriptor, formData);

    if (result) {
      closePopup();
      setIsSending(false);
    } else {
      setIsSending(false);
      setStep(AddToCartStep.ERROR_SENDING);
    }
  };

  useLayoutEffect(() => {
    if (!productDescriptor.base.quantityVisible && isLoadedState) {
      submit();
    }
  }, [isLoadedState, productDescriptor.base.quantityVisible]);

  const wizardContextProps = {
    closePopup,
    editMode: editMode ?? false,
    restrictionQuery,
    step,
    setStep,
    productDescriptor,
  };

  return (
    <WizardContextProvider {...wizardContextProps}>
      <FormProvider {...formProps}>
        {step === AddToCartStep.LOADING && <LoadingModal />}
        {step === AddToCartStep.ERROR_LOADING && <ErrorLoadingModal productDescriptor={productDescriptor} />}
        {step === AddToCartStep.LOADED_CONSUMER && (
          <ConsumerViewAvailabilityModal productDescriptor={productDescriptor} />
        )}
        {step === AddToCartStep.LOADED_DEFAULT && (
          <AddToCartModal productDescriptor={productDescriptor} submit={submit} sending={isSending} />
        )}

        {step === AddToCartStep.ERROR_SENDING && <ErrorSendingModal productDescriptor={productDescriptor} />}
      </FormProvider>
    </WizardContextProvider>
  );
};

function getInitialStep(productDescriptor: ProductDescriptor): AddToCartStep {
  if (
    userHasPermission(Permission.AVAILABILITY) &&
    !userHasPermission(Permission.PRICE) &&
    productDescriptor.availability
  ) {
    return AddToCartStep.LOADED_CONSUMER;
  }
  if (productDescriptor.isLoaded) {
    return AddToCartStep.LOADED_DEFAULT;
  }
  if (productDescriptor.isError) {
    return AddToCartStep.ERROR_LOADING;
  }
  return AddToCartStep.LOADING;
}
